import { flow } from '@helo/utils'
import { GridColumns, GridRows } from '@visx/grid'

import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type {
  ChartCartesianOutput,
  ChartCartesianSlice,
} from '../ChartCartesian'
import { useChartWrapperContext } from '../ChartWrapper/ChartWrapper'
import { ChartGridStyles } from './ChartGrid.constants'
import { type ChartGridOptions, chartGridOptions } from './ChartGrid.options'
import {
  ChartGridInputParts,
  ChartGridOutputParts,
  ChartGridUtils,
} from './ChartGrid.parts'

export { chartGridOptions } from './ChartGrid.options'
export type { ChartGridOptions } from './ChartGrid.options'

/** Pipeline input consumed by ChartGrid.setup. */
export type ChartGridInput = ChartGridOptions

export type ChartGridSlice = {
  showXGridlines: boolean
  showYGridlines: boolean
}

/** Pipeline output slice — namespaced grid config. */
export type ChartGridOutput = {
  grid: ChartGridSlice
}

export interface ChartGridRenderProps
  extends ChartGridSlice,
    Partial<Pick<ChartCartesianSlice, 'yTicks'>> {}

const chartGridSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartGridInput,
    ChartGridOutput,
    Pick<QueryOutput, 'contentNode'>
  >(
    flow(
      ChartGridInputParts.setupInput(),
      ChartGridInputParts.setup_xAxisInput(),
      ChartGridInputParts.setup_yAxisInput(),
      ChartGridOutputParts.setGrid(),
    ),
  )

export interface ChartGridProps {
  renderConfig: ChartGridOutput & Partial<ChartCartesianOutput>
}

const Grid = ({ renderConfig }: ChartGridProps) => {
  const { innerWidth, innerHeight, scales } = useChartWrapperContext()
  const { grid, cartesian } = renderConfig
  if (grid == null) return null
  const { showYGridlines, showXGridlines } = grid
  const yTicks = cartesian?.yTicks

  if (scales == null) return null

  if (scales.orientation === 'horizontal') {
    const columnTicks = ChartGridUtils.columnMeasureTicksAwayFromAxes({
      ticks: yTicks,
      xScale: value => scales.xScale(value),
      innerWidth,
    })

    return (
      <>
        {showXGridlines ? (
          <GridColumns
            scale={scales.xScale}
            height={innerHeight}
            tickValues={columnTicks}
            stroke={ChartGridStyles.stroke}
            strokeDasharray={ChartGridStyles.strokeDasharray}
            strokeWidth={ChartGridStyles.strokeWidth}
          />
        ) : null}
      </>
    )
  }

  const rowTicks = ChartGridUtils.rowTicksAwayFromAxes({
    ticks: yTicks,
    yScale: value => scales.yScale(value),
    innerHeight,
  })

  const columnTicks = ChartGridUtils.columnTicksAwayFromAxes({
    domain: scales.xScale.domain(),
    xScale: value => scales.xScale(value),
    innerWidth,
  })

  return (
    <>
      {showYGridlines ? (
        <GridRows
          scale={scales.yScale}
          width={innerWidth}
          tickValues={rowTicks}
          stroke={ChartGridStyles.stroke}
          strokeDasharray={ChartGridStyles.strokeDasharray}
          strokeWidth={ChartGridStyles.strokeWidth}
        />
      ) : null}
      {showXGridlines ? (
        <GridColumns
          scale={scales.xScale}
          height={innerHeight}
          tickValues={columnTicks}
          stroke={ChartGridStyles.stroke}
          strokeDasharray={ChartGridStyles.strokeDasharray}
          strokeWidth={ChartGridStyles.strokeWidth}
        />
      ) : null}
    </>
  )
}

export const ChartGrid = {
  options: chartGridOptions,
  Grid,
  setup: chartGridSetup,
}
